import { ReactNode } from 'react';
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';

export type ColumnConfig = {
  field: string;
  headerName: string;
  type: 'string' | 'number' | 'date' | 'currency' | 'image' | 'clickable';
  formatter?: string;
  onClick?: string;
  [key: string]: unknown;
};

// Utility to get nested value by path (e.g., "Sector.name")
const getValueByPath = (obj: unknown, path: string) =>
  path
    .split('.')
    .reduce((acc, part) => acc && (acc as Record<string, unknown>)[part], obj);

export const generateAgGridColumns = (
  configs: ColumnConfig[],
  {
    formatters = {},
    callbacks = {},
  }: {
    formatters?: Record<string, (value: unknown, data: unknown) => unknown>;
    callbacks?: Record<string, (row: unknown) => void>;
  } = {},
): ColDef[] => {
  return configs.map((col) => {
    // Only spread extra config keys that are not field/headerName/type/formatter/onClick
    const { field, headerName, type, formatter, onClick, ...extra } = col;
    const colDef: ColDef = {
      field,
      headerName,
      sortable: true,
      filter: true,
      ...extra,
      valueGetter: (params: ValueGetterParams) =>
        getValueByPath(params.data, field),
    };

    // Attach formatter if specified
    if (formatter && formatters[formatter]) {
      colDef.valueFormatter = (params: ValueFormatterParams) =>
        String(
          formatters[formatter](
            getValueByPath(params.data, field),
            params.data,
          ),
        );
    } else if (type === 'date') {
      // Default date formatting
      colDef.valueFormatter = (params: ValueFormatterParams) => {
        const value = getValueByPath(params.data, field);
        return value ? new Date(value as string).toLocaleDateString() : '';
      };
    }

    // Attach clickable cell renderer if specified
    if (type === 'clickable' && onClick && callbacks[onClick]) {
      colDef.cellRenderer = (params: ICellRendererParams) => {
        const value = getValueByPath(params.data, field);
        return (
          <span
            style={{
              color: 'var(--primary-color)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            onClick={() => callbacks[onClick](params.data)}
          >
            {value as ReactNode}
          </span>
        );
      };
    }

    // Attach image renderer if specified
    if (type === 'image') {
      colDef.cellRenderer = (params: ICellRendererParams) => {
        const value = getValueByPath(params.data, field);
        return value ? (
          <img
            src={value as string}
            alt={headerName}
            style={{ maxHeight: 32, maxWidth: 32 }}
          />
        ) : null;
      };
    }

    return colDef;
  });
};
